import React, { useState, useEffect } from 'react';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { getAllUsers } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://aia74-backend-production.up.railway.app/api' });
API.interceptors.request.use(cfg => {
  const token = localStorage.getItem('aia74_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export default function Sidebar({ onSelectUser }) {
  const { conversations, activeUser, selectUser, onlineUsers } = useChat();
  const { logout } = useAuth();
  const [search,     setSearch]     = useState('');
  const [allUsers,   setAllUsers]   = useState([]);
  const [userLabels, setUserLabels] = useState({});

  useEffect(() => {
    getAllUsers()
      .then(({ data }) => {
        const users = Array.isArray(data) ? data : data.users || [];
        setAllUsers(users);
        const labelsMap = {};
        Promise.all(users.map(async u => {
          try {
            const r = await API.get(`/labels/${u._id}`);
            labelsMap[u._id] = Array.isArray(r.data) ? r.data : [];
          } catch(e) { labelsMap[u._id] = []; }
        })).then(() => setUserLabels({ ...labelsMap }));
      })
      .catch(console.error);
  }, []);

  const merged = allUsers.map(user => {
    const conv = conversations.find(c => c.userId?._id === user._id);
    return {
      _id:             conv?._id || user._id,
      userId:          conv?.userId || user,
      lastMessage:     conv?.lastMessage || null,
      lastMessageTime: conv?.lastMessageTime || null,
      unreadCount:     conv?.unreadCount || 0,
      hasConversation: !!conv,
    };
  });

  const sorted = [...merged].sort((a, b) => {
    if (b.unreadCount !== a.unreadCount) return b.unreadCount - a.unreadCount;
    if (a.lastMessageTime && b.lastMessageTime)
      return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
    if (a.lastMessageTime) return -1;
    if (b.lastMessageTime) return 1;
    return (a.userId?.name || '').localeCompare(b.userId?.name || '');
  });

  const filtered = sorted.filter(c => {
    const q = search.toLowerCase();
    if (!q) return true;
    const matchName  = c.userId?.name?.toLowerCase().includes(q);
    const matchEmail = c.userId?.email?.toLowerCase().includes(q);
    const matchLabel = (userLabels[c.userId?._id] || []).some(l =>
      l.text?.toLowerCase().includes(q)
    );
    return matchName || matchEmail || matchLabel;
  });

  const getInitials = (name = '') =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const handleSelect = (user) => {
    selectUser(user);
    if (onSelectUser) onSelectUser();
  };

  const onlineCount = Object.values(onlineUsers).filter(Boolean).length;

  return (
    <div style={s.sidebar}>
      <div style={s.header}>
        <div style={s.brand}>
          <span style={{ fontSize: '18px' }}>⚡</span>
          <span style={s.brandText}>AIA74</span>
        </div>
        <button onClick={logout} style={s.logoutBtn} title="Logout">⏻</button>
      </div>

      <div style={s.searchWrap}>
        <input
          type="text"
          placeholder="🔍  Search players or labels..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={s.search}
        />
      </div>

      <div style={s.statsBar}>
        <span style={s.stat}><span style={s.statDot} />{onlineCount} Online</span>
        <span style={s.stat}>{allUsers.length} Total</span>
      </div>

      <div style={s.list}>
        {filtered.length === 0 && <div style={s.empty}>No users found</div>}
        {filtered.map(conv => {
          const user     = conv.userId;
          if (!user) return null;
          const isActive  = activeUser?._id === user._id;
          const isOnline  = onlineUsers[user._id];
          const hasUnread = conv.unreadCount > 0;
          const noChat    = !conv.hasConversation;
          const labels    = userLabels[user._id] || [];

          return (
            <div key={conv._id} onClick={() => handleSelect(user)}
              style={{ ...s.convItem, ...(isActive ? s.convItemActive : {}) }}>
              <div style={s.avatarWrap}>
                <div style={{
                  ...s.avatar,
                  background: isActive ? 'linear-gradient(135deg,#00C8FF,#1E6FFF)' : noChat ? 'rgba(255,255,255,0.06)' : 'linear-gradient(135deg,#1e3a4a,#0d2233)',
                  color: isActive ? '#080c14' : '#00C8FF',
                  border: noChat && !isActive ? '1px dashed rgba(0,200,255,0.2)' : 'none',
                }}>
                  {getInitials(user.name)}
                </div>
                {isOnline && <div style={s.onlineDot} />}
              </div>

              <div style={s.convInfo}>
                <div style={s.convTop}>
                  <span style={{ ...s.convName, color: isActive ? '#00C8FF' : '#e2e8f0' }}>{user.name}</span>
                  <span style={s.convTime}>
                    {conv.lastMessageTime ? formatDistanceToNow(new Date(conv.lastMessageTime), { addSuffix: false }) : ''}
                  </span>
                </div>

                {labels.length > 0 && (
                  <div style={s.labelsRow}>
                    {labels.slice(0, 2).map(l => (
                      <span key={l._id} style={{ ...s.labelChip, borderColor: l.color, color: l.color }}>
                        {l.text}
                      </span>
                    ))}
                    {labels.length > 2 && <span style={s.moreLabels}>+{labels.length - 2}</span>}
                  </div>
                )}

                <div style={s.convBottom}>
                  <span style={{ ...s.convPreview, color: noChat ? 'rgba(0,200,255,0.35)' : 'rgba(255,255,255,.35)', fontStyle: noChat ? 'italic' : 'normal' }}>
                    {conv.lastMessage || (noChat ? 'Tap to start chat' : 'No messages yet')}
                  </span>
                  {hasUnread && <span style={s.badge}>{conv.unreadCount}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const s = {
  sidebar:        { width: '300px', minWidth: '300px', background: '#0d1420', borderRight: '1px solid rgba(0,200,255,.1)', display: 'flex', flexDirection: 'column', height: '100%', fontFamily: "'Exo 2',sans-serif" },
  header:         { padding: '16px', borderBottom: '1px solid rgba(0,200,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  brand:          { display: 'flex', alignItems: 'center', gap: '8px' },
  brandText:      { fontFamily: "'Rajdhani',sans-serif", fontSize: '18px', fontWeight: '700', color: '#00C8FF', letterSpacing: '4px' },
  logoutBtn:      { background: 'rgba(255,50,50,.1)', border: '1px solid rgba(255,50,50,.2)', borderRadius: '8px', color: '#ff6b6b', fontSize: '15px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  searchWrap:     { padding: '10px 14px', borderBottom: '1px solid rgba(0,200,255,.05)', flexShrink: 0 },
  search:         { width: '100%', background: 'rgba(255,255,255,.04)', border: '1px solid rgba(0,200,255,.15)', borderRadius: '10px', padding: '9px 13px', color: '#fff', fontSize: '13px', fontFamily: "'Exo 2',sans-serif", outline: 'none', boxSizing: 'border-box' },
  statsBar:       { padding: '7px 14px', display: 'flex', gap: '14px', borderBottom: '1px solid rgba(0,200,255,.05)', flexShrink: 0 },
  stat:           { fontSize: '11px', color: 'rgba(255,255,255,.35)', display: 'flex', alignItems: 'center', gap: '5px', letterSpacing: '1px' },
  statDot:        { width: '6px', height: '6px', borderRadius: '50%', background: '#00C8FF', boxShadow: '0 0 6px #00C8FF' },
  list:           { flex: 1, overflowY: 'auto', padding: '6px 0' },
  empty:          { textAlign: 'center', color: 'rgba(255,255,255,.2)', fontSize: '13px', padding: '40px 20px' },
  convItem:       { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '11px 14px', cursor: 'pointer', borderLeft: '3px solid transparent', transition: 'all .15s' },
  convItemActive: { background: 'rgba(0,200,255,.06)', borderLeft: '3px solid #00C8FF' },
  avatarWrap:     { position: 'relative', flexShrink: 0, marginTop: '2px' },
  avatar:         { width: '42px', height: '42px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', fontFamily: "'Rajdhani',sans-serif" },
  onlineDot:      { position: 'absolute', bottom: '-1px', right: '-1px', width: '10px', height: '10px', borderRadius: '50%', background: '#00C8FF', border: '2px solid #0d1420', boxShadow: '0 0 6px #00C8FF' },
  convInfo:       { flex: 1, minWidth: 0 },
  convTop:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' },
  convName:       { fontSize: '13px', fontWeight: '600' },
  convTime:       { fontSize: '10px', color: 'rgba(255,255,255,.25)', flexShrink: 0 },
  labelsRow:      { display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '3px' },
  labelChip:      { fontSize: '9px', fontWeight: '700', letterSpacing: '0.5px', padding: '1px 6px', borderRadius: '8px', border: '1px solid', background: 'rgba(255,255,255,0.03)', whiteSpace: 'nowrap' },
  moreLabels:     { fontSize: '9px', color: 'rgba(255,255,255,0.25)', fontWeight: '600', alignSelf: 'center' },
  convBottom:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  convPreview:    { fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '170px' },
  badge:          { background: 'linear-gradient(135deg,#00C8FF,#1E6FFF)', color: '#080c14', fontSize: '10px', fontWeight: '700', borderRadius: '10px', padding: '2px 6px', flexShrink: 0 },
};